import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  IconButton,
  TextField,
  Stack,
  Grid,
  MenuItem,
  Typography,
  Checkbox,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import ClearIcon from '@mui/icons-material/Clear';
import { useState, useMemo, useEffect, useCallback } from 'react';
import * as S from './styles';
import { ModalProps } from './type';

import {
  createClassSection,
  updateClassSection,
  fetchRegisteredStudents,
  searchAllStudents,
} from 'apis/main';

type StudentRow = {
  student_no: string;
  student_uuid: number;
  student_name: string;
  student_id: string;
  is_in_class_section: 0 | 1;
};

export default function Modal(props: ModalProps) {
  const {
    modalType, // only 'create' | 'update'
    sectionId,
    open,
    onClose,
    userId,
    collegeId,
    initialData,
  } = props;

  const [formData, setFormData] = useState({
    college_id: collegeId,
    class_section_year: '',
    class_section_semester: '',
    class_section_subject: '',
    class_section_grade: '',
    class_section_name: '',
    class_section_teacher_info: '',
  });

  const [showLeft, setShowLeft] = useState(false);
  const [leftItems, setLeftItems] = useState<StudentRow[]>([]);
  const [rightItems, setRightItems] = useState<StudentRow[]>([]);
  const [leftChecked, setLeftChecked] = useState<Set<number>>(new Set());
  const [rightChecked, setRightChecked] = useState<Set<number>>(new Set());

  const [searchType, setSearchType] = useState<1 | 2 | 3>(1);
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    if (modalType === 'update' && initialData) {
      setFormData({
        college_id: collegeId,
        class_section_year: String(initialData.class_section_year || ''),
        class_section_semester: String(
          initialData.class_section_semester || ''
        ),
        class_section_subject: String(initialData.class_section_subject || ''),
        class_section_grade: String(initialData.class_section_grade || ''),
        class_section_name: String(initialData.class_section_name || ''),
        class_section_teacher_info: String(
          initialData.class_section_teacher_info || ''
        ),
      });
    } else if (modalType === 'create') {
      setFormData({
        college_id: collegeId,
        class_section_year: '',
        class_section_semester: '',
        class_section_subject: '',
        class_section_grade: '',
        class_section_name: '',
        class_section_teacher_info: '',
      });
    }
  }, [modalType, initialData, collegeId]);

  useEffect(() => {
    if (!open) return;

    const init = async () => {
      try {
        if (modalType === 'update' && userId && sectionId) {
          const res = await fetchRegisteredStudents({
            user_id: Number(userId),
            class_section_id: Number(sectionId),
          });
          const items: StudentRow[] =
            res.data?.student_class_section_search_infos ?? [];
          setRightItems(items);
        } else {
          setRightItems([]);
        }
      } catch (e) {
        console.error('Failed to fetch registered students', e);
      } finally {
        setShowLeft(false);
        setLeftItems([]);
        setLeftChecked(new Set());
        setRightChecked(new Set());
      }
    };

    init();
  }, [open, modalType, userId, sectionId]);

  const updateFormData = useMemo(
    () => ({
      class_section_id: sectionId,
      class_section_create_id: userId,
      ...formData,
    }),
    [sectionId, userId, formData]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSearch = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await searchAllStudents({
        user_id: Number(userId),
        class_section_id: sectionId ? Number(sectionId) : undefined,
        searchType,
        keyword,
      });
      const items: StudentRow[] =
        res.data?.student_class_section_search_infos ?? [];
      setLeftItems(items);
      setShowLeft(true);
    } catch (e) {
      console.error('Failed to search students', e);
    }
  }, [userId, sectionId, searchType, keyword]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const toggleLeft = (uuid: number) => {
    setLeftChecked(prev => {
      const next = new Set(prev);
      next.has(uuid) ? next.delete(uuid) : next.add(uuid);
      return next;
    });
  };

  const toggleRight = (uuid: number) => {
    setRightChecked(prev => {
      const next = new Set(prev);
      next.has(uuid) ? next.delete(uuid) : next.add(uuid);
      return next;
    });
  };

  const handleToggleAllLeft = () => {
    if (leftChecked.size === leftItems.length) {
      setLeftChecked(new Set());
    } else {
      setLeftChecked(new Set(leftItems.map(i => i.student_uuid)));
    }
  };

  const handleToggleAllRight = () => {
    if (rightChecked.size === rightItems.length) {
      setRightChecked(new Set());
    } else {
      setRightChecked(new Set(rightItems.map(i => i.student_uuid)));
    }
  };

  const handleAddSelected = () => {
    if (leftChecked.size === 0) return;
    const toAdd = leftItems.filter(i => leftChecked.has(i.student_uuid));
    setRightItems(prev => {
      const existing = new Set(prev.map(p => p.student_uuid));
      const merged = [...prev];
      toAdd.forEach(it => {
        if (!existing.has(it.student_uuid)) merged.push(it);
      });
      return merged;
    });
    setLeftItems(prev => prev.filter(i => !leftChecked.has(i.student_uuid)));
    setLeftChecked(new Set());
  };

  const handleAddOne = (item: StudentRow) => {
    setRightItems(prev => {
      const exists = prev.some(p => p.student_uuid === item.student_uuid);
      return exists ? prev : [...prev, item];
    });
    setLeftItems(prev =>
      prev.filter(i => i.student_uuid !== item.student_uuid)
    );
    setLeftChecked(prev => {
      const next = new Set(prev);
      next.delete(item.student_uuid);
      return next;
    });
  };

  const handleDeleteSelected = () => {
    if (rightChecked.size === 0) return;
    setRightItems(prev => prev.filter(i => !rightChecked.has(i.student_uuid)));
    setRightChecked(new Set());
  };

  const handleDeleteOne = (uuid: number) => {
    setRightItems(prev => prev.filter(i => i.student_uuid !== uuid));
    setRightChecked(prev => {
      const next = new Set(prev);
      next.delete(uuid);
      return next;
    });
  };

  const handleSubmit = async () => {
    try {
      if (!userId) return;

      const studentUuids = rightItems.map(i => i.student_uuid);

      if (modalType === 'create') {
        const payload = {
          ...formData,
          class_section_student_list: studentUuids,
        };
        await createClassSection(Number(userId), payload);
      } else if (modalType === 'update' && sectionId) {
        const payload = {
          ...updateFormData,
          class_section_student_list: studentUuids,
        };
        await updateClassSection(Number(userId), payload);
      }
      onClose();
    } catch (error) {
      console.error(
        `Failed to ${
          modalType === 'create' ? 'create' : 'update'
        } class section:`,
        error
      );
    }
  };

  const renderFormFields = (data: typeof formData) => (
    <Grid container spacing={1}>
      <Grid item xs={6}>
        <TextField
          fullWidth
          size="small"
          margin="dense"
          name="class_section_year"
          label="Year"
          value={data.class_section_year}
          onChange={handleChange}
          InputLabelProps={{ sx: { fontSize: 14 } }}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          select
          fullWidth
          size="small"
          margin="dense"
          name="class_section_semester"
          label="Semester"
          value={data.class_section_semester}
          onChange={handleChange}
          InputLabelProps={{ sx: { fontSize: 14 } }}
        >
          <MenuItem value="1">1</MenuItem>
          <MenuItem value="2">2</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          size="small"
          margin="dense"
          name="class_section_subject"
          label="Course name"
          value={data.class_section_subject}
          onChange={handleChange}
          InputLabelProps={{ sx: { fontSize: 14 } }}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          select
          fullWidth
          size="small"
          margin="dense"
          name="class_section_grade"
          label="Year level"
          value={data.class_section_grade}
          onChange={handleChange}
          InputLabelProps={{ sx: { fontSize: 14 } }}
        >
          {[1, 2, 3, 4].map(g => (
            <MenuItem key={g} value={String(g)}>
              {g}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          size="small"
          margin="dense"
          name="class_section_name"
          label="Class section"
          value={data.class_section_name}
          onChange={handleChange}
          InputLabelProps={{ sx: { fontSize: 14 } }}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          size="small"
          margin="dense"
          name="class_section_teacher_info"
          label="Instructor name"
          value={data.class_section_teacher_info}
          onChange={handleChange}
          InputLabelProps={{ sx: { fontSize: 14 } }}
        />
      </Grid>
    </Grid>
  );

  const renderContent = () => {
    switch (modalType) {
      case 'update':
      case 'create':
        return (
          <>
            <DialogTitle>
              {modalType === 'create'
                ? 'Create class section'
                : 'Edit class section'}
              <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <FormControl fullWidth>
                  {renderFormFields(formData)}
                </FormControl>
              </Stack>
            </DialogContent>
          </>
        );
      default:
        return null;
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      {renderContent()}

      {/* Student search/registration area (shared for create/update) */}
      <DialogContent>
        <S.SearchWrapper>
          <span className="title">Practicum student list</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <TextField
              select
              size="small"
              label="Search by"
              value={searchType}
              onChange={e => setSearchType(Number(e.target.value) as 1 | 2 | 3)}
              sx={{ width: 120 }}
            >
              <MenuItem value={1}>Name</MenuItem>
              <MenuItem value={2}>Student ID</MenuItem>
              <MenuItem value={3}>Email</MenuItem>
            </TextField>
            <TextField
              size="small"
              placeholder="Enter keyword"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onKeyPress={handleKeyPress}
              sx={{ minWidth: 240 }}
            />
            <Button variant="contained" onClick={handleSearch}>
              Search
            </Button>
          </div>
        </S.SearchWrapper>

        <S.SectionsContainer>
          <S.SectionWrap>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 4,
              }}
            >
              <span className="title">Student list (this school)</span>
              <Button
                size="small"
                variant="outlined"
                onClick={handleAddSelected}
                disabled={!showLeft || leftItems.length === 0}
              >
                Add
              </Button>
            </div>

            <S.SearchSection>
              {showLeft ? (
                <>
                  <S.ListHeader $bg="#fff">
                    <S.CheckboxCell>
                      <Checkbox
                        checked={
                          leftItems.length > 0 &&
                          leftChecked.size === leftItems.length
                        }
                        indeterminate={
                          leftChecked.size > 0 &&
                          leftChecked.size < leftItems.length
                        }
                        onChange={handleToggleAllLeft}
                      />
                    </S.CheckboxCell>
                    <S.HeaderCell>Student ID</S.HeaderCell>
                    <S.HeaderCell>Name</S.HeaderCell>
                    <S.HeaderCell $hasBorderBefore>Email</S.HeaderCell>
                  </S.ListHeader>
                  {leftItems.map((item, index) => (
                    <S.ListRow
                      key={item.student_uuid}
                      $isLast={index === leftItems.length - 1}
                      $highlighted={item.is_in_class_section === 1}
                      onDoubleClick={() => handleAddOne(item)}
                    >
                      <S.CheckboxCell>
                        <Checkbox
                          checked={leftChecked.has(item.student_uuid)}
                          onChange={() => toggleLeft(item.student_uuid)}
                        />
                      </S.CheckboxCell>
                      <S.DataCell>{item.student_no}</S.DataCell>
                      <S.DataCell>{item.student_name}</S.DataCell>
                      <S.DataCell $hasBorderBefore>
                        {item.student_id}
                      </S.DataCell>
                    </S.ListRow>
                  ))}
                </>
              ) : (
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', p: 2, textAlign: 'center' }}
                >
                  Run a search to display the student list.
                </Typography>
              )}
            </S.SearchSection>
          </S.SectionWrap>

          <SyncAltIcon sx={{ color: '#14855B', fontSize: 32 }} />

          <S.SectionWrap>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 4,
              }}
            >
              <span className="title">Registered student list</span>
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={handleDeleteSelected}
              >
                Delete
              </Button>
            </div>

            <S.RegisteredSection>
              <S.ListHeader $bg="#F7FFFC">
                <S.CheckboxCell>
                  <Checkbox
                    checked={
                      rightItems.length > 0 &&
                      rightChecked.size === rightItems.length
                    }
                    indeterminate={
                      rightChecked.size > 0 &&
                      rightChecked.size < rightItems.length
                    }
                    onChange={handleToggleAllRight}
                  />
                </S.CheckboxCell>
                <S.HeaderCell>Student ID</S.HeaderCell>
                <S.HeaderCell>Name</S.HeaderCell>
                <S.HeaderCell $width="1">Email</S.HeaderCell>
                <S.HeaderCell $width="0.3"></S.HeaderCell>
              </S.ListHeader>
              {rightItems.map((item, index) => (
                <S.ListRow
                  key={item.student_uuid}
                  $isLast={index === rightItems.length - 1}
                >
                  <S.CheckboxCell>
                    <Checkbox
                      checked={rightChecked.has(item.student_uuid)}
                      onChange={() => toggleRight(item.student_uuid)}
                    />
                  </S.CheckboxCell>
                  <S.DataCell>{item.student_no}</S.DataCell>
                  <S.DataCell>{item.student_name}</S.DataCell>
                  <S.DataCell $width="1">{item.student_id}</S.DataCell>
                  <S.DataCell $width="0.3">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteOne(item.student_uuid)}
                      sx={{
                        color: '#d32f2f',
                        '&:hover': {
                          backgroundColor: 'rgba(211, 47, 47, 0.08)',
                        },
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </S.DataCell>
                </S.ListRow>
              ))}
            </S.RegisteredSection>
          </S.SectionWrap>
        </S.SectionsContainer>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          {modalType === 'create' ? 'Create' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
